import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Activity, Wifi, WifiOff, Clock, TrendingUp, TrendingDown,
  Minus, Shield, RefreshCw, AlertTriangle, Zap
} from 'lucide-react';
import { api } from '../api/client';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isMercadoAbierto() {
  const now  = new Date();
  const day  = now.getUTCDay(); // 0=dom, 6=sab
  const hour = now.getUTCHours();
  // Forex: lunes 00:00 UTC – viernes 22:00 UTC (aprox)
  if (day === 0) return false;
  if (day === 6) return false;
  if (day === 5 && hour >= 22) return false;
  return true;
}

function formatPrice(val, decimals = 2) {
  if (val == null) return '—';
  return parseFloat(val).toFixed(decimals);
}

function PriceChange({ current, prev }) {
  if (!prev || !current) return null;
  const diff = parseFloat(current) - parseFloat(prev);
  if (Math.abs(diff) < 0.0001) return null;
  const up = diff > 0;
  return (
    <span className={`text-[10px] font-mono ml-1 ${up ? 'text-green-400' : 'text-red-400'}`}>
      {up ? '▲' : '▼'} {Math.abs(diff).toFixed(2)}
    </span>
  );
}

function Countdown({ nextCycleMs }) {
  const [remaining, setRemaining] = useState(nextCycleMs);

  useEffect(() => {
    setRemaining(nextCycleMs);
    const t = setInterval(() => setRemaining(r => Math.max(0, r - 1000)), 1000);
    return () => clearInterval(t);
  }, [nextCycleMs]);

  const secs = Math.floor(remaining / 1000);
  const mins = Math.floor(secs / 60);
  const s    = secs % 60;
  return (
    <span className="font-mono text-amber-400 tabular-nums">
      {mins}:{String(s).padStart(2, '0')}
    </span>
  );
}

// ─── Card de precio ────────────────────────────────────────────────────────────

function PriceCard({ symbol, data, prevData }) {
  const bid = data?.bid ?? data?.price;
  const ask = data?.ask;
  const spread = bid && ask ? (parseFloat(ask) - parseFloat(bid)).toFixed(2) : null;

  return (
    <div className="bg-[#13131A] border border-[#1E1E2E] rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">{symbol}</span>
        {data
          ? <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          : <span className="w-1.5 h-1.5 rounded-full bg-slate-600" />
        }
      </div>

      {data ? (
        <>
          <div className="flex items-baseline gap-1">
            <span className="font-mono font-bold text-2xl text-slate-100">
              {formatPrice(bid)}
            </span>
            <PriceChange current={bid} prev={prevData?.bid ?? prevData?.price} />
          </div>
          <div className="flex items-center justify-between mt-2">
            {ask && (
              <span className="text-[10px] font-mono text-slate-600">
                Ask: <span className="text-slate-400">{formatPrice(ask)}</span>
              </span>
            )}
            {spread && (
              <span className="text-[10px] font-mono text-slate-600">
                Spread: <span className="text-amber-500/70">{spread}</span>
              </span>
            )}
          </div>
        </>
      ) : (
        <p className="text-slate-600 font-mono text-sm">Sin datos</p>
      )}
    </div>
  );
}

// ─── Card de estrategia con últimos indicadores ───────────────────────────────

function StrategyCard({ strategy, lastDecision, nextCycleMs }) {
  const cfg = {
    BUY:  { color: 'text-green-400', icon: TrendingUp  },
    SELL: { color: 'text-red-400',   icon: TrendingDown },
    HOLD: { color: 'text-slate-400', icon: Minus        },
  };
  const dec    = lastDecision?.decision || 'HOLD';
  const dcfg   = cfg[dec] || cfg.HOLD;
  const Icon   = dcfg.icon;
  const rsi    = lastDecision?.rsiM5 ? parseFloat(lastDecision.rsiM5).toFixed(1) : '—';
  const conf   = lastDecision?.confidence ? (parseFloat(lastDecision.confidence) * 100).toFixed(0) : '—';
  const price  = lastDecision?.price ? parseFloat(lastDecision.price).toFixed(2) : '—';

  const rsiColor = lastDecision?.rsiM5
    ? parseFloat(lastDecision.rsiM5) > 60 ? 'text-green-400'
    : parseFloat(lastDecision.rsiM5) < 40 ? 'text-red-400'
    : 'text-slate-400'
    : 'text-slate-600';

  return (
    <div className={`bg-[#13131A] border rounded-lg p-4 transition-colors ${
      strategy.active ? 'border-[#1E1E2E]' : 'border-[#1E1E2E] opacity-50'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-1.5 h-1.5 rounded-full ${strategy.active ? 'bg-green-400 animate-pulse' : 'bg-slate-600'}`} />
          <span className="font-mono text-sm font-bold text-slate-200">{strategy.name}</span>
        </div>
        <span className="text-[10px] font-mono text-amber-500/60">{strategy.symbol}</span>
      </div>

      {/* Última decisión */}
      <div className="flex items-center gap-3 mb-3">
        <div className={`flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-mono font-bold ${dcfg.color} bg-current/10`}
          style={{ backgroundColor: dec === 'BUY' ? 'rgba(74,222,128,0.1)' : dec === 'SELL' ? 'rgba(248,113,113,0.1)' : 'rgba(148,163,184,0.1)' }}>
          <Icon size={10} />
          {dec}
        </div>
        <span className="text-[10px] font-mono text-slate-500">
          {conf}% conf · ${price}
        </span>
      </div>

      {/* Indicadores */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="bg-[#0A0A0F] rounded p-2">
          <p className="text-[9px] font-mono text-slate-600 uppercase mb-0.5">RSI M5</p>
          <p className={`text-xs font-mono font-bold ${rsiColor}`}>{rsi}</p>
        </div>
        <div className="bg-[#0A0A0F] rounded p-2">
          <p className="text-[9px] font-mono text-slate-600 uppercase mb-0.5">Latencia</p>
          <p className="text-xs font-mono text-slate-300">
            {lastDecision?.latencyMs ? `${lastDecision.latencyMs}ms` : '—'}
          </p>
        </div>
      </div>

      {/* Próximo ciclo */}
      {strategy.active && nextCycleMs != null && (
        <div className="flex items-center justify-between pt-2 border-t border-[#1E1E2E]">
          <span className="text-[10px] font-mono text-slate-600 flex items-center gap-1">
            <Clock size={9} /> Próximo ciclo
          </span>
          <Countdown nextCycleMs={nextCycleMs} />
        </div>
      )}
    </div>
  );
}

// ─── Risk Governor card ───────────────────────────────────────────────────────

function RiskCard({ status, account }) {
  const balance    = account ? parseFloat(account.balance || 0) : 10000;
  const dailyLimit = balance * 0.05; // 5% FTMO daily limit
  const trades     = status?.dailyTrades || 0;
  const maxTrades  = 5;
  const pct        = Math.min((trades / maxTrades) * 100, 100);
  const color      = pct >= 80 ? 'bg-red-400' : pct >= 60 ? 'bg-amber-400' : 'bg-green-400';

  return (
    <div className="bg-[#13131A] border border-[#1E1E2E] rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Shield size={13} className="text-amber-400" />
        <span className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">Risk Governor</span>
      </div>

      <div className="space-y-3">
        <div>
          <div className="flex justify-between text-[10px] font-mono mb-1">
            <span className="text-slate-500">Trades hoy</span>
            <span className="text-slate-300">{trades} / {maxTrades}</span>
          </div>
          <div className="h-1 bg-[#1E1E2E] rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="flex justify-between text-[10px] font-mono">
          <span className="text-slate-500">Balance</span>
          <span className="text-slate-200">${balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </div>

        <div className="flex justify-between text-[10px] font-mono">
          <span className="text-slate-500">Límite diario (5%)</span>
          <span className="text-red-400/70">${dailyLimit.toFixed(2)}</span>
        </div>

        <div className="flex justify-between text-[10px] font-mono">
          <span className="text-slate-500">Modo</span>
          <span className="text-amber-400">
            {status?.profile || 'FTMO_CHALLENGE'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Bridge status ────────────────────────────────────────────────────────────

function BridgeStatus({ connected, lastUpdate }) {
  return (
    <div className="flex items-center gap-2">
      {connected
        ? <Wifi size={13} className="text-green-400" />
        : <WifiOff size={13} className="text-red-400" />
      }
      <span className={`text-[11px] font-mono ${connected ? 'text-green-400' : 'text-red-400'}`}>
        Bridge {connected ? 'conectado' : 'desconectado'}
      </span>
      {lastUpdate && (
        <span className="text-[10px] font-mono text-slate-600 ml-2">
          <RefreshCw size={9} className="inline mr-1" />
          {lastUpdate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </span>
      )}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

const SYMBOLS = ['XAUUSD', 'EURUSD', 'USDMXN'];

export default function Monitor() {
  const [prices,      setPrices]      = useState({});
  const [prevPrices,  setPrevPrices]  = useState({});
  const [strategies,  setStrategies]  = useState([]);
  const [decisions,   setDecisions]   = useState([]); // últimas decisiones por estrategia
  const [status,      setStatus]      = useState(null);
  const [riskStatus,  setRiskStatus]  = useState(null);
  const [account,     setAccount]     = useState(null);
  const [connected,   setConnected]   = useState(false);
  const [lastUpdate,  setLastUpdate]  = useState(null);
  const [mercadoOpen, setMercadoOpen] = useState(isMercadoAbierto());

  const priceRef  = useRef(null);
  const statusRef = useRef(null);

  // ── Cargar datos estáticos una vez ─────────────────────────────────
  useEffect(() => {
    Promise.all([
      api.getStrategies(),
      api.getDecisions({ limit: 20 }),
    ]).then(([strat, dec]) => {
      setStrategies(strat.data);
      setDecisions(dec.data);
    }).catch(() => {});
  }, []);

  // ── Polling de precios cada 3s ──────────────────────────────────────
  const pollPrices = useCallback(() => {
    setMercadoOpen(isMercadoAbierto());
    api.getPrice('XAUUSD,EURUSD,USDMXN')
      .then(r => {
        // El endpoint devuelve { prices: "string" } — necesitamos los precios del bridge
        // Usamos getLivePositions para verificar conectividad y datos del bridge
        setConnected(true);
        setLastUpdate(new Date());
      })
      .catch(() => setConnected(false));

    // Precios individuales desde el bridge via agent
	api.getPricesLive('XAUUSD,EURUSD,USDMXN')
	  .then(r => {
	    setPrevPrices(prices);
	    setPrices(r.data);
	    setConnected(true);
	    setLastUpdate(new Date());
	  })
	  .catch(() => setConnected(false));
  }, [prices]);

  useEffect(() => {
    pollPrices();
    priceRef.current = setInterval(pollPrices, 3000);
    return () => clearInterval(priceRef.current);
  }, [pollPrices]);

  // ── Polling de status cada 10s ──────────────────────────────────────
  const pollStatus = useCallback(() => {
    Promise.all([
      api.getStatus(),
      api.getRiskStatus?.() || Promise.resolve({ data: null }),
      api.getAccount(),
    ]).then(([st, rs, acc]) => {
      setStatus(st.data);
      setRiskStatus(rs.data);
      // Account viene como JSON ahora
      setAccount(typeof acc.data === 'object' ? acc.data : null);
      setConnected(true);
      setLastUpdate(new Date());
    }).catch(() => setConnected(false));
  }, []);

  useEffect(() => {
    pollStatus();
    statusRef.current = setInterval(pollStatus, 10000);
    return () => clearInterval(statusRef.current);
  }, [pollStatus]);

  // ── Recargar decisiones cada 30s ────────────────────────────────────
  useEffect(() => {
    const t = setInterval(() => {
      api.getDecisions({ limit: 20 })
        .then(r => setDecisions(r.data))
        .catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, []);

  // ── Última decisión por estrategia ──────────────────────────────────
  const lastDecisionByStrategy = {};
  decisions.forEach(d => {
    if (!lastDecisionByStrategy[d.strategyId]) {
      lastDecisionByStrategy[d.strategyId] = d;
    }
  });

  // ── Countdown por estrategia ────────────────────────────────────────
  const strategyStatus = status?.strategies || [];
  const nextCycleByStrategy = {};
  strategyStatus.forEach(s => {
    const strat = strategies.find(st => st.id === s.strategyId);
    if (strat?.intervalSeconds) {
      const lastDec = lastDecisionByStrategy[s.strategyId];
      if (lastDec?.timestamp) {
        const lastMs  = new Date(lastDec.timestamp).getTime();
        const elapsed = Date.now() - lastMs;
        const interval = strat.intervalSeconds * 1000;
        nextCycleByStrategy[s.strategyId] = Math.max(0, interval - elapsed);
      }
    }
  });

  return (
    <div className="p-8 space-y-8">

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-100 tracking-tight flex items-center gap-3">
            <Zap size={20} className="text-amber-400" />
            Monitor en vivo
          </h1>
          <p className="text-xs text-slate-500 font-mono mt-1">
            {new Date().toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })} CDMX
          </p>
        </div>
        <BridgeStatus connected={connected} lastUpdate={lastUpdate} />
      </div>

      {/* Mercado cerrado banner */}
      {!mercadoOpen && (
        <div className="flex items-center gap-3 bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3">
          <AlertTriangle size={14} className="text-slate-500 shrink-0" />
          <div>
            <p className="text-[11px] font-mono text-slate-400 font-bold">MERCADO CERRADO</p>
            <p className="text-[10px] font-mono text-slate-600">
              Forex opera lunes 12am – viernes 5pm CDMX. Los precios pueden no actualizarse.
            </p>
          </div>
        </div>
      )}

      {/* ── PRECIOS ────────────────────────────────────────────── */}
      <div>
        <p className="text-[10px] font-mono text-slate-600 uppercase tracking-wider mb-3">
          Precios · actualiza cada 3s
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {SYMBOLS.map(sym => (
            <PriceCard
              key={sym}
              symbol={sym}
              data={prices[sym]}
              prevData={prevPrices[sym]}
            />
          ))}
        </div>
      </div>

      {/* ── ESTRATEGIAS ────────────────────────────────────────── */}
      <div>
        <p className="text-[10px] font-mono text-slate-600 uppercase tracking-wider mb-3">
          Estrategias · última decisión Claude
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {strategies.map(s => (
            <StrategyCard
              key={s.id}
              strategy={s}
              lastDecision={lastDecisionByStrategy[s.id]}
              nextCycleMs={nextCycleByStrategy[s.id]}
            />
          ))}
        </div>
      </div>

      {/* ── RISK GOVERNOR ──────────────────────────────────────── */}
      <div>
        <p className="text-[10px] font-mono text-slate-600 uppercase tracking-wider mb-3">
          Risk Governor · estado actual
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <RiskCard status={riskStatus} account={account} />

          {/* Agent status */}
          <div className="bg-[#13131A] border border-[#1E1E2E] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Activity size={13} className="text-amber-400" />
              <span className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">Estado del agente</span>
            </div>
            <div className="space-y-2">
              {strategyStatus.length === 0 ? (
                <p className="text-[11px] font-mono text-slate-600">Sin estrategias activas</p>
              ) : (
                strategyStatus.map(s => (
                  <div key={s.strategyId} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${s.running ? 'bg-green-400 animate-pulse' : s.paused ? 'bg-amber-400' : 'bg-slate-600'}`} />
                      <span className="text-[11px] font-mono text-slate-400">{s.strategyName}</span>
                    </div>
                    <span className={`text-[10px] font-mono ${
                      s.running ? 'text-green-400' : s.paused ? 'text-amber-400' : 'text-slate-600'
                    }`}>
                      {s.running ? 'RUNNING' : s.paused ? 'PAUSED' : 'IDLE'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
